import React, { useState, useEffect } from "react";
import { BehavioralSubject, Level } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import CustomAlert from "../../../CustomUi/CustomAlert";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import TextAreaInput from "../../../CustomUi/TextAreaInput";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useSubjectService } from "../../../../hooks/Domain/useSubjectService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  behavioralSubjectToEdit: BehavioralSubject | null;
  behavioralSubjectLevel: Level | null;
}

const BehavioralSubjectModal: React.FC<Props> = ({
  isOpen,
  onClose,
  behavioralSubjectToEdit,
  behavioralSubjectLevel,
}) => {
  const {
    createBehavioralSubject,
    updateBehavioralSubject,
    deleteBehavioralSubject,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useSubjectService();

  const getInitialBehavioralSubjectFormState = (): BehavioralSubject => ({
    id: "",
    name: "",
    description: "",
    levelId: behavioralSubjectLevel?.id || "",
    schoolId: "",
    createdAt: "",
    updatedAt: "",
  });

  const initialBehavioralSubjectFormState =
    getInitialBehavioralSubjectFormState();

  const { levels } = useSchoolsContext();
  const icons = useIcons();
  const [behavioralSubjectFormState, setBehavioralSubjectFormState] =
    useState<BehavioralSubject>(initialBehavioralSubjectFormState);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    name: { type: "text" as const, required: true, maxLength: 100 },
    levelId: { type: "text" as const, required: true },
    description: { type: "text" as const, required: false, maxLength: 500 },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: behavioralSubjectToEdit,
      currentData: behavioralSubjectFormState,
      isEditMode: !!behavioralSubjectToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setBehavioralSubjectFormState,
    setTouchedFields,
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof BehavioralSubject;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (behavioralSubjectToEdit) {
      setBehavioralSubjectFormState(behavioralSubjectToEdit);
    } else {
      setBehavioralSubjectFormState(getInitialBehavioralSubjectFormState());
    }
    setTouchedFields(new Set());
  }, [behavioralSubjectToEdit, behavioralSubjectLevel, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      behavioralSubjectFormState[fieldName as keyof BehavioralSubject]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      behavioralSubjectFormState[fieldName as keyof BehavioralSubject]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (behavioralSubjectToEdit) {
      const response = await updateBehavioralSubject(
        behavioralSubjectToEdit.id,
        behavioralSubjectFormState
      );
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createBehavioralSubject(
        behavioralSubjectFormState
      );
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!behavioralSubjectToEdit) return;
    const response = await deleteBehavioralSubject(behavioralSubjectToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && behavioralSubjectToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const levelOptions = levels.map((level) => ({
    label: level.levelName,
    value: level.id,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related behavioral subject records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={
          behavioralSubjectToEdit
            ? "Edit Behavioral Subject"
            : "Add New Behavioral Subject"
        }
        icon={icons.main.subject}
        mode={behavioralSubjectToEdit ? "edit" : "add"}
        actionText={
          behavioralSubjectToEdit ? "Save Changes" : "Save Behavioral Subject"
        }
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Behavioral Subject Information Section */}
          <Box>
            <InlineSeparator label="Behavioral Subject Information" />
            <VStack gap={4}>
              <TextInput
                label="Behavioral Subject Name"
                name="name"
                type="text"
                value={behavioralSubjectFormState.name}
                onChange={handleFieldChange}
                placeholder="Behavioral Subject Name"
                required
                error={showFieldError("name") ? getFieldError("name") : ""}
                invalid={showFieldError("name")}
              />

              {behavioralSubjectToEdit && (
                <SelectInput
                  label="Behavioral Subject Level"
                  name="levelId"
                  value={behavioralSubjectFormState.levelId}
                  onChange={handleFieldChange}
                  collection={levelOptions}
                  placeholder="Select Behavioral Subject Level"
                  required
                  error={
                    showFieldError("levelId") ? getFieldError("levelId") : ""
                  }
                  invalid={showFieldError("levelId")}
                />
              )}
            </VStack>
          </Box>

          {/* Additional Information Section */}
          <Box>
            <InlineSeparator label="Additional Information" />
            <TextAreaInput
              label="Description"
              name="description"
              value={behavioralSubjectFormState.description}
              onChange={handleFieldChange}
              rows={3}
              error={
                showFieldError("description")
                  ? getFieldError("description")
                  : ""
              }
              invalid={showFieldError("description")}
            />
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default BehavioralSubjectModal;
